/**
 * Sidebar v2 thread row: the two-line card for pinned and active threads,
 * dispatching to the slim row for the snoozed and settled shelves.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (SidebarThreadRow card variant, ~lines 1666-1981) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: subscriptions and derived values live in
 * sidebarV2RowModel, shared parts in sidebarV2RowParts, the slim variant in
 * sidebarV2RowSlim; change requests, terminal state, worker fan-out, and
 * branch display come from forge's shared row modules (see the model's
 * docblock for the seams).
 */
import { memo } from "react";
import { TerminalIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import { Tooltip, TooltipTrigger } from "~/components/ui/tooltip";
import { synchronizeTerminalPulse } from "~/components/ThreadStatusIndicators";
import { SidebarThreadPullRequestBadge } from "~/components/sidebar/SidebarThreadPullRequestBadge";
import { JumpHintBadge } from "./sidebarV2Chrome";
import { useSidebarV2RowModel, type SidebarV2RowModel } from "./sidebarV2RowModel";
import { useSortableRootProps, WorkerFanOut } from "./sidebarV2RowParts";
import {
  DragDestination,
  HoverActions,
  PinIndicator,
  StatusEntry,
  Title,
} from "./sidebarV2RowSlots";
import { SidebarV2RowSlim } from "./sidebarV2RowSlim";
import { SidebarV2Tooltip } from "./sidebarV2Tooltip";
import {
  SidebarV2BranchMeta,
  SidebarV2OriginIcon,
  SidebarV2ProjectIcon,
  SidebarV2ProviderIcon,
} from "./SidebarV2IdentityMeta";

export const SidebarV2Row = memo(function SidebarV2Row(
  props: import("./sidebarV2RowParts").SidebarV2RowProps,
) {
  if (props.variant === "slim") {
    return <SidebarV2RowSlim {...props} />;
  }
  return <SidebarV2RowCard {...props} />;
});

/** The row's live terminal-state glyph. */
function TerminalStatusIcon(props: {
  readonly model: SidebarV2RowModel;
  readonly threadId: string;
}) {
  if (props.model.terminalStatus === null) return null;
  return (
    <span
      role="img"
      aria-label={`${props.model.terminalProcessCount} terminal ${props.model.terminalProcessCount === 1 ? "process" : "processes"} running`}
      data-testid={`sidebar-terminal-status-${props.threadId}`}
      className={cn(
        "inline-flex shrink-0 items-center justify-center",
        props.model.terminalStatus.colorClass,
      )}
    >
      <TerminalIcon
        className={cn(
          "size-3.5",
          props.model.terminalStatus.pulse && "motion-safe:animate-status-pulse",
        )}
        onAnimationStart={synchronizeTerminalPulse}
      />
    </span>
  );
}

/** The card's meta line: branch, terminal state, and the PR badge. */
function MetaLine(props: {
  readonly model: SidebarV2RowModel;
  readonly threadId: string;
  readonly linkedPrUrl: string | null;
  readonly threadOrigin?: import("~/components/sidebar/threadOrigin").ThreadOrigin;
  readonly provider: string | null | undefined;
  readonly onOpenPullRequest: (event: React.MouseEvent<HTMLElement>, url: string) => void;
}) {
  const { model } = props;
  const prBadge =
    model.pr !== null || props.linkedPrUrl !== null ? (
      <SidebarThreadPullRequestBadge
        badge={{
          number: model.pr?.number ?? null,
          url: model.pr?.url ?? props.linkedPrUrl ?? null,
        }}
        status={model.linkedPrIndicator}
        onOpenPullRequest={props.onOpenPullRequest}
      />
    ) : null;
  return (
    <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-secondary-label">
      <SidebarV2BranchMeta branch={model.displayBranch} className="text-muted-foreground/40" />
      <TerminalStatusIcon model={model} threadId={props.threadId} />
      <SidebarV2OriginIcon origin={props.threadOrigin ?? "current-device"} />
      <SidebarV2ProviderIcon provider={props.provider} className="size-3.5" />
      {prBadge}
    </div>
  );
}

/** Surface classes for the card row: selection, recede, and drag states. */
export function cardSurfaceClass(
  props: import("./sidebarV2RowParts").SidebarV2RowProps,
  model: SidebarV2RowModel,
  dragging: boolean,
): string {
  return cn(
    "group/sidebar-row relative w-full cursor-pointer overflow-hidden rounded-md px-2 text-left text-sidebar-foreground outline-none select-none hover:bg-sidebar-row-hover",
    props.isActive && "bg-sidebar-row-active",
    model.isSelected && "bg-sidebar-row-selected",
    model.shouldRecede && "text-sidebar-muted-foreground/75 hover:text-sidebar-foreground",
    model.shouldRecede &&
      (model.status === "working" || model.status === "monitoring") &&
      "opacity-70 transition-opacity hover:opacity-100 focus-within:opacity-100 motion-reduce:transition-none",
    dragging &&
      "bg-sidebar bg-linear-to-b from-sidebar-row-active to-sidebar-row-active opacity-100 shadow-lg",
  );
}

/** The card's first line: project name, pin indicator, drag badge, status
 * entry, and the hover actions. */
function CardFirstLine(props: {
  readonly model: SidebarV2RowModel;
  readonly projectDisplayName: string | null;
  readonly projectCwd: string | null;
  readonly projectCanonicalIdentity:
    | import("~/components/ProjectIcon").ProjectCanonicalIdentity
    | undefined;
  readonly isPinned: boolean;
  readonly dragging: boolean;
  readonly dropVerb: string | null;
  readonly thread: import("~/types").SidebarThreadSummary;
  readonly timestampFormat: import("@cmmd-forge/contracts").TimestampFormat;
  readonly onSnooze: (
    threadRef: import("@cmmd-forge/contracts").ScopedThreadRef,
    preset: { readonly snoozedUntil: string },
  ) => void;
}) {
  const { model } = props;
  return (
    <div className="flex h-5 min-w-0 items-center gap-1.5">
      {props.projectDisplayName ? (
        <SidebarV2ProjectIcon
          environmentId={props.thread.environmentId}
          cwd={props.projectCwd}
          name={props.projectDisplayName}
          canonicalIdentity={props.projectCanonicalIdentity}
          className="size-4 shrink-0"
        />
      ) : null}
      {props.projectDisplayName ? (
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-xs font-medium text-secondary-label",
            model.shouldRecede && "font-normal",
          )}
        >
          {props.projectDisplayName}
        </span>
      ) : (
        <span className="flex-1" />
      )}
      {props.isPinned ? <PinIndicator dragging={props.dragging} unpin={model.unpin} /> : null}
      <DragDestination dropVerb={props.dropVerb} dragging={props.dragging} />
      <span className="group/sidebar-status-slot relative ml-auto flex h-5 min-w-8 shrink-0 items-stretch justify-end text-xs">
        <StatusEntry model={model} thread={props.thread} />
        <HoverActions
          model={model}
          timestampFormat={props.timestampFormat}
          onSnooze={props.onSnooze}
        />
      </span>
    </div>
  );
}

/** The card's tooltip body: first line, title, meta, worker fan-out, and
 * the jump hint. */
function CardBody(props: {
  readonly model: SidebarV2RowModel;
  readonly rowProps: import("./sidebarV2RowParts").SidebarV2RowProps;
  readonly dragging: boolean;
}) {
  const { model, rowProps } = props;
  const thread = rowProps.thread;
  return (
    <>
      <div className="relative z-10 h-[4.875rem] px-(--sidebar-row-content-inset) py-(--sidebar-content-inset)">
        <CardFirstLine
          model={model}
          projectDisplayName={rowProps.projectDisplayName}
          projectCwd={rowProps.projectCwd}
          projectCanonicalIdentity={rowProps.projectCanonicalIdentity}
          isPinned={rowProps.isPinned}
          dragging={props.dragging}
          dropVerb={rowProps.dropVerb}
          thread={thread}
          timestampFormat={rowProps.timestampFormat}
          onSnooze={rowProps.onSnooze}
        />
        <div className="mt-1 flex min-w-0">
          <Title
            model={model}
            threadTitle={thread.title}
            isRenaming={rowProps.isRenaming}
            renamingTitle={rowProps.renamingTitle}
            onRenameTitleChange={rowProps.onRenameTitleChange}
          />
        </div>
        <MetaLine
          model={model}
          threadId={thread.id}
          linkedPrUrl={rowProps.linkedPrUrl}
          threadOrigin={rowProps.threadOrigin ?? "current-device"}
          provider={thread.modelSelection?.provider}
          onOpenPullRequest={rowProps.onOpenPullRequest}
        />
      </div>
      {rowProps.workerRows.length > 0 ? (
        <WorkerFanOut
          threadKey={model.threadKey}
          workerRows={rowProps.workerRows}
          onNavigateWorker={rowProps.onNavigateWorker}
        />
      ) : null}
      {rowProps.jumpLabel ? <JumpHintBadge label={rowProps.jumpLabel} /> : null}
    </>
  );
}

/** The card's tooltip chrome: trigger element, body, and the hover card. */
function CardTooltipShell(props: {
  readonly model: SidebarV2RowModel;
  readonly rowProps: import("./sidebarV2RowParts").SidebarV2RowProps;
  readonly dragging: boolean;
}) {
  const { model, rowProps, dragging } = props;
  return (
    <Tooltip disabled={model.snoozeMenuOpen || dragging}>
      <TooltipTrigger
        render={
          <div
            ref={model.rowRef}
            role="button"
            tabIndex={0}
            data-testid="sidebar-row-card"
            className={cardSurfaceClass(rowProps, model, dragging)}
            onClick={model.handleClick}
            onDoubleClick={model.handleDoubleClick}
            onKeyDown={model.handleKeyDown}
            onContextMenu={model.handleContextMenu}
          />
        }
      >
        <CardBody model={model} rowProps={rowProps} dragging={dragging} />
      </TooltipTrigger>
      <SidebarV2Tooltip
        thread={rowProps.thread}
        projectDisplayName={rowProps.projectDisplayName}
        environmentLabel={rowProps.environmentLabel}
        environmentMachine={rowProps.environmentMachine}
        modelLabel={
          rowProps.thread.session?.provider ?? rowProps.thread.modelSelection?.model ?? null
        }
        terminalStatus={model.terminalStatus}
        terminalProcessCount={model.terminalProcessCount}
        pullRequestEntries={model.prEntries}
        projectCwd={rowProps.projectCwd}
        projectCanonicalIdentity={rowProps.projectCanonicalIdentity}
        displayBranch={model.displayBranch}
        threadOrigin={rowProps.threadOrigin ?? "current-device"}
      />
    </Tooltip>
  );
}

/** The pinned/active card row. */
function SidebarV2RowCard(props: import("./sidebarV2RowParts").SidebarV2RowProps) {
  const model = useSidebarV2RowModel(props);
  const sortable = props.sortable;
  const sortableRootProps = useSortableRootProps(sortable);
  const dragging = sortable?.isDragging === true;

  return (
    <li
      data-thread-item
      {...sortableRootProps}
      className={cn(
        "list-none py-0.5 [content-visibility:auto] [contain-intrinsic-size:auto_78px]",
        dragging && "relative z-20",
      )}
    >
      <CardTooltipShell model={model} rowProps={props} dragging={dragging} />
    </li>
  );
}
